'use client';
import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { useLanguage } from '@/lib/LanguageContext';
import { getTranslations } from '@/lib/i18n';

const Footer: React.FC = () => {
  const { language } = useLanguage();
  const t = getTranslations(language);
  const h = t.home;
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const serviceLinks = language === 'fr'
    ? [
        { label: 'Développement Logiciel', href: '/servicios/desarrollo-software' },
        { label: 'Infrastructure IT', href: '/servicios/infraestructura-redes' },
        { label: 'Cybersécurité', href: '/servicios/ciberseguridad' },
        { label: 'Recyclage Électronique', href: '/servicios/reciclaje-electronico' },
        { label: 'Équipement Technologique', href: '/servicios/equipamiento-tecnologico' },
        { label: 'Conseil et Consulting', href: '/servicios/asesoria-consultoria' },
      ]
    : [
        { label: 'Desarrollo de Software', href: '/servicios/desarrollo-software' },
        { label: 'Infraestructura TI', href: '/servicios/infraestructura-redes' },
        { label: 'Ciberseguridad', href: '/servicios/ciberseguridad' },
        { label: 'Reciclaje Electrónico', href: '/servicios/reciclaje-electronico' },
        { label: 'Equipamiento Tecnológico', href: '/servicios/equipamiento-tecnologico' },
        { label: 'Asesoría y Consultoría', href: '/servicios/asesoria-consultoria' },
      ];

  const navLinks = language === 'fr'
    ? [
        { label: 'Accueil', href: '/' },
        { label: 'À propos', href: '/about' },
        { label: 'Portfolio', href: '/#portfolio' },
        { label: 'Actualités', href: '/#noticias' },
        { label: 'FAQ', href: '/faq' },
        { label: 'Contact', href: '/contact' },
      ]
    : [
        { label: 'Inicio', href: '/' },
        { label: 'Nosotros', href: '/about' },
        { label: 'Portfolio', href: '/#portfolio' },
        { label: 'Noticias', href: '/#noticias' },
        { label: 'FAQ', href: '/faq' },
        { label: 'Contáctenos', href: '/contact' },
      ];

  const footerHeading = language === 'fr'
    ? ['Construire le', 'futur', 'numérique', 'du Chili.']
    : ['Construyendo el', 'futuro', 'digital', 'de Chile.'];

  const ctaTitle = language === 'fr' ? 'Commencez votre transformation' : 'Inicia tu transformación';
  const ctaSubtitle = language === 'fr' ?'Contactez-nous pour découvrir comment INNOBOX peut transformer votre entreprise.' :'Contáctanos para descubrir cómo INNOBOX puede transformar tu empresa.';
  const ctaBtn = language === 'fr' ? 'Nous Contacter' : 'Contáctenos';
  const servicesLabel = language === 'fr' ? 'Services' : 'Servicios';
  const navigationLabel = language === 'fr' ? 'Navigation' : 'Navegación';
  const contactLabel = language === 'fr' ? 'Contact' : 'Contacto';
  const copyright = language === 'fr' ? '© 2025 INNOBOX. Tous droits réservés.' : '© 2025 INNOBOX. Todos los Derechos Reservados.';

  return (
    <footer id="contacto" ref={sectionRef} className="relative overflow-hidden" style={{ backgroundColor: '#141618' }}>
      
      {/* CTA Section */}
      <div
        className="relative overflow-hidden"
        style={{ backgroundColor: '#202427', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        {/* Grid lines */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-0 bottom-0 left-[25%] w-px bg-white/5" />
          <div className="absolute top-0 bottom-0 left-[50%] w-px bg-white/5" />
          <div className="absolute top-0 bottom-0 left-[75%] w-px bg-white/5" />
        </div>
        {/* Accent glow */}
        <div
          className="absolute top-0 right-0 w-[500px] h-[300px] pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse, rgba(171,210,51,0.08) 0%, transparent 70%)',
            transform: 'translate(20%, -20%)',
          }}
        />
        <div className="max-w-[1280px] mx-auto px-6 py-20 relative z-10">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-10">
            <div className="max-w-xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-6 h-px bg-[#abd233]" />
                <span style={{ fontSize: '12px', letterSpacing: '0.1em', color: '#abd233', textTransform: 'uppercase' }}>
                  {ctaTitle}
                </span>
              </div>
              <p
                className="text-white/60"
                style={{ fontSize: '16px', lineHeight: 1.6 }}>
                {ctaSubtitle}
              </p>
            </div>
            <a
              href="/contact"
              className="group relative inline-flex items-center gap-3 overflow-hidden flex-shrink-0"
              style={{
                border: '1px solid #abd233',
                padding: '16px 32px',
                fontSize: '13px',
                color: '#abd233',
                letterSpacing: '0.02em',
                transition: 'all 300ms ease-out',
              }}>
              <span
                className="absolute inset-0 bg-[#abd233] transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300"
                style={{ zIndex: 0 }}
              />
              <span className="relative z-10 transition-colors duration-300 group-hover:text-[#141618]">
                {ctaBtn}
              </span>
              <svg className="relative z-10 transition-colors duration-300 group-hover:text-[#141618]" width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M1 7h12M8 2l5 5-5 5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="relative" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        {/* Grid lines */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-0 bottom-0 left-[25%] w-px bg-white/4" />
          <div className="absolute top-0 bottom-0 left-[50%] w-px bg-white/4" />
          <div className="absolute top-0 bottom-0 left-[75%] w-px bg-white/4" />
        </div>

        <div className="max-w-[1280px] mx-auto px-6 pt-20 pb-12 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 mb-16">
            {/* Large heading */}
            <div className="lg:col-span-5">
              {/* Logo */}
              <div className="flex items-center gap-3 mb-8">
                <div className="grid grid-cols-3 gap-[4px]">
                  {[...Array(9)].map((_, i) => (
                    <div
                      key={i}
                      className="w-[5px] h-[5px] rounded-full"
                      style={{ backgroundColor: i === 0 || i === 2 || i === 6 || i === 8 ? '#abd233' : 'rgba(255,255,255,0.3)' }}
                    />
                  ))}
                </div>
                <span className="text-white font-medium" style={{ fontSize: '16px', letterSpacing: '-0.01em' }}>INNOBOX</span>
              </div>

              <div
                className={`transition-all duration-700 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}>
                {footerHeading.map((line, i) => (
                  <div
                    key={i}
                    className="overflow-hidden"
                    style={{ transitionDelay: `${i * 100}ms` }}>
                    <span
                      className={i === 1 ? 'text-[#abd233]' : 'text-white'}
                      style={{
                        display: 'block',
                        fontSize: 'clamp(32px, 4vw, 56px)',
                        fontWeight: 400,
                        lineHeight: 1.05,
                        letterSpacing: '-0.02em',
                      }}>
                      {line}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Nav columns */}
            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-10">
                {/* Services */}
                <div>
                  <h5
                    className="text-white/40 mb-6"
                    style={{ fontSize: '11px', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                    {servicesLabel}
                  </h5>
                  <ul className="space-y-0">
                    {serviceLinks.map((link, i) => (
                      <li key={i}>
                        <Link href={link.href} className="cipher-footer-link">
                          {link.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Navigation */}
                <div>
                  <h5
                    className="text-white/40 mb-6"
                    style={{ fontSize: '11px', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                    {navigationLabel}
                  </h5>
                  <ul className="space-y-0">
                    {navLinks.map((link, i) => (
                      <li key={i}>
                        <Link href={link.href} className="cipher-footer-link">
                          {link.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Contact */}
                <div>
                  <h5
                    className="text-white/40 mb-6"
                    style={{ fontSize: '11px', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                    {contactLabel}
                  </h5>
                  <div className="space-y-4">
                    <div>
                      <div className="text-white/30 mb-1" style={{ fontSize: '11px', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                        Email
                      </div>
                      <a
                        href="mailto:info@innobox.cl"
                        className="text-white/60 hover:text-[#abd233] transition-colors duration-200"
                        style={{ fontSize: '14px' }}>
                        info@innobox.cl
                      </a>
                    </div>
                    <div>
                      <div className="text-white/30 mb-1" style={{ fontSize: '11px', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                        WhatsApp
                      </div>
                      <a
                        href="https://wa.me/56991001317"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-white/60 hover:text-[#abd233] transition-colors duration-200"
                        style={{ fontSize: '14px' }}>
                        +56 9 9100 1317
                      </a>
                    </div>
                    <div>
                      <div className="text-white/30 mb-1" style={{ fontSize: '11px', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                        {language === 'fr' ? 'Certifications' : 'Credenciales'}
                      </div>
                      <div className="flex gap-2 mt-2">
                        <img
                          src="https://innobox.cl/wp-content/uploads/2021/06/scrum-master-professional-certificate-smpc.2-100x100.png"
                          alt="Scrum Master Professional Certificate - certificación ágil del equipo Innobox"
                          className="w-10 h-10 rounded opacity-60 hover:opacity-100 transition-opacity"
                        />
                        <img
                          src="https://innobox.cl/wp-content/uploads/2021/06/cyber-security-foundation-professional-certificate-.1-100x100.png"
                          alt="Cyber Security Foundation Professional Certificate - certificación de ciberseguridad Innobox"
                          className="w-10 h-10 rounded opacity-60 hover:opacity-100 transition-opacity"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div
            className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-8"
            style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <span className="text-white/25" style={{ fontSize: '12px', letterSpacing: '0.02em' }}>
              {copyright}
            </span>
            <div className="flex items-center gap-6">
              {/* Social links */}
              <a
                href="https://www.linkedin.com/company/innobox-cl/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/30 hover:text-[#abd233] transition-colors duration-200"
                aria-label="LinkedIn">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
              </a>
              <a
                href="https://wa.me/56991001317"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/30 hover:text-[#abd233] transition-colors duration-200"
                aria-label="WhatsApp">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;